import { BOARD_VIEW_TYPE } from '@/features/tasks/public';
import { FIELD_TYPE } from '@/features/custom-fields/public';
import { TEMPLATE_SECTOR } from '../types';
import type {
  ProjectTemplate,
  ProjectTemplateSummary,
  TemplateBoard,
  TemplateView,
} from '../types';

/**
 * Every system preset's Board view is the Kanban board and is always the default —
 * confirmed in Figma (`tasks/project-templates-backend-contract.md` §4/§5).
 */
function withBoardView(...extra: Array<Pick<TemplateView, 'type' | 'name'>>): TemplateView[] {
  return [
    { type: BOARD_VIEW_TYPE.Kanban, name: 'Board', isDefault: true },
    ...extra.map((view) => ({ ...view, isDefault: false })),
  ];
}

/** Every mock preset/template has exactly one board — the real backend allows more, but none of ours need it. */
function singleBoard(
  board: Omit<TemplateBoard, 'description' | 'order' | 'ungroupedTasks'>,
): TemplateBoard[] {
  return [{ ...board, description: null, order: 0, ungroupedTasks: [] }];
}

export const projectTemplatesMockState: ProjectTemplate[] = [
  {
    id: '1',
    tenantId: null,
    title: '2-Week Sprint',
    sector: TEMPLATE_SECTOR.Technology,
    description:
      'A standard 2-week engineering sprint with planning, development, QA and closeout.',
    isSystem: true,
    createdById: 'system',
    createdAt: '2026-01-05T09:00:00Z',
    updatedAt: '2026-01-05T09:00:00Z',
    customFields: [
      {
        name: 'Story points',
        type: FIELD_TYPE.Select,
        options: ['1', '2', '3', '5', '8', '13'],
      },
      {
        name: 'Sprint',
        type: FIELD_TYPE.Select,
        options: ['Sprint 1', 'Sprint 2', 'Sprint 3', 'Sprint 4'],
      },
      { name: 'Environment', type: FIELD_TYPE.Select, options: ['Dev', 'QA', 'Prod'] },
    ],
    tags: [
      { name: 'Bug', color: 'red-ember' },
      { name: 'Feature', color: 'blue-azure' },
      { name: 'Tech debt', color: 'orange-amber' },
      { name: 'Blocked', color: 'red-ember' },
    ],
    boards: singleBoard({
      name: 'Main board',
      statuses: [
        { name: 'Backlog', color: 'gray-slate', type: 'Todo', order: 0 },
        { name: 'In Development', color: 'blue-azure', type: 'InProgress', order: 1 },
        { name: 'QA', color: 'yellow-amber', type: 'InProgress', order: 2 },
        { name: 'Ready for Release', color: 'green-emerald', type: 'Done', order: 3 },
      ],
      views: withBoardView(
        { type: BOARD_VIEW_TYPE.List, name: 'List' },
        { type: BOARD_VIEW_TYPE.Gantt, name: 'Gantt' },
      ),
      groups: [
        {
          name: 'Planning',
          order: 0,
          tasks: [
            { title: 'Sprint kickoff', priority: 'medium' },
            { title: 'Refine backlog', priority: 'medium' },
            { title: 'Define acceptance criteria', priority: 'high' },
          ],
        },
        {
          name: 'In Development',
          order: 1,
          tasks: [
            { title: 'Implement feature A', priority: 'high' },
            { title: 'Implement feature B', priority: 'high' },
            { title: 'Code review', priority: 'medium' },
          ],
        },
        {
          name: 'QA',
          order: 2,
          tasks: [
            { title: 'Functional testing', priority: 'high' },
            { title: 'Regression testing', priority: 'medium' },
            { title: 'Bug fixing', priority: 'high' },
          ],
        },
        {
          name: 'Closeout',
          order: 3,
          tasks: [
            { title: 'Sprint demo', priority: 'medium' },
            { title: 'Retrospective', priority: 'low' },
            { title: 'Next sprint planning', priority: 'low' },
          ],
        },
      ],
    }),
  },
  {
    id: '2',
    tenantId: null,
    title: 'Campaign Launch',
    sector: TEMPLATE_SECTOR.Marketing,
    description: 'Plan, produce and launch a marketing campaign end to end.',
    isSystem: true,
    createdById: 'system',
    createdAt: '2026-01-05T09:00:00Z',
    updatedAt: '2026-01-05T09:00:00Z',
    customFields: [
      {
        name: 'Channel',
        type: FIELD_TYPE.Select,
        options: ['Social media', 'Email', 'Blog', 'Ads'],
      },
      { name: 'Budget', type: FIELD_TYPE.Number, options: null },
      { name: 'Target KPI', type: FIELD_TYPE.Text, options: null },
    ],
    tags: [
      { name: 'Content', color: 'blue-azure' },
      { name: 'Paid', color: 'purple-violet' },
      { name: 'Organic', color: 'green-emerald' },
      { name: 'Urgent', color: 'red-ember' },
    ],
    boards: singleBoard({
      name: 'Main board',
      statuses: [
        { name: 'To Define', color: 'gray-slate', type: 'Todo', order: 0 },
        { name: 'In Design', color: 'purple-violet', type: 'InProgress', order: 1 },
        { name: 'In Review', color: 'blue-azure', type: 'InProgress', order: 2 },
        { name: 'Published', color: 'green-emerald', type: 'Done', order: 3 },
      ],
      views: withBoardView({ type: BOARD_VIEW_TYPE.Calendar, name: 'Editorial Calendar' }),
      groups: [
        {
          name: 'Strategy',
          order: 0,
          tasks: [
            { title: 'Define campaign objectives', priority: 'high' },
            { title: 'Research target audience', priority: 'medium' },
            { title: 'Set budget', priority: 'high' },
            { title: 'Choose distribution channels', priority: 'medium' },
          ],
        },
        {
          name: 'Creative',
          order: 1,
          tasks: [
            { title: 'Design creative concept', priority: 'high' },
            { title: 'Write core copy', priority: 'medium' },
            { title: 'Produce graphic assets', priority: 'high' },
            { title: 'Record promotional video', priority: 'medium' },
            { title: 'Review brand identity', priority: 'low' },
          ],
        },
        {
          name: 'Execution',
          order: 2,
          tasks: [
            { title: 'Schedule social media posts', priority: 'high' },
            { title: 'Set up ads campaign', priority: 'high' },
            { title: 'Send email marketing', priority: 'medium' },
            { title: 'Publish blog post', priority: 'medium' },
            { title: 'Coordinate with influencers', priority: 'medium' },
            { title: 'Launch landing page', priority: 'high' },
          ],
        },
        {
          name: 'Analysis',
          order: 3,
          tasks: [
            { title: 'Monitor live metrics', priority: 'medium' },
            { title: 'Generate results report', priority: 'medium' },
            { title: 'Campaign retrospective', priority: 'low' },
          ],
        },
      ],
    }),
  },
  {
    id: '3',
    tenantId: null,
    title: 'Client Onboarding',
    sector: TEMPLATE_SECTOR.ProfessionalServices,
    description: 'Onboard a new client from welcome to first check-in.',
    isSystem: true,
    createdById: 'system',
    createdAt: '2026-01-05T09:00:00Z',
    updatedAt: '2026-01-05T09:00:00Z',
    customFields: [
      {
        name: 'Plan tier',
        type: FIELD_TYPE.Select,
        options: ['Starter', 'Pro', 'Enterprise'],
      },
      { name: 'Kickoff date', type: FIELD_TYPE.Date, options: null },
      { name: 'Primary contact', type: FIELD_TYPE.Text, options: null },
    ],
    tags: [
      { name: 'Welcome', color: 'blue-azure' },
      { name: 'Churn risk', color: 'red-ember' },
      { name: 'VIP', color: 'yellow-amber' },
      { name: 'Blocked', color: 'gray-slate' },
    ],
    boards: singleBoard({
      name: 'Main board',
      statuses: [
        { name: 'New', color: 'gray-slate', type: 'Todo', order: 0 },
        { name: 'In Progress', color: 'blue-azure', type: 'InProgress', order: 1 },
        { name: 'Completed', color: 'green-emerald', type: 'Done', order: 2 },
      ],
      views: withBoardView({ type: BOARD_VIEW_TYPE.List, name: 'List by Stage' }),
      groups: [
        {
          name: 'Welcome',
          order: 0,
          tasks: [
            { title: 'Send welcome email', priority: 'high' },
            { title: 'Schedule kickoff call', priority: 'high' },
            { title: 'Share getting-started guide', priority: 'medium' },
          ],
        },
        {
          name: 'Setup',
          order: 1,
          tasks: [
            { title: 'Set up client account', priority: 'high' },
            { title: 'Import initial data', priority: 'medium' },
            { title: 'Verify access and permissions', priority: 'medium' },
          ],
        },
        {
          name: 'Follow-up',
          order: 2,
          tasks: [
            { title: '30-day review', priority: 'medium' },
            { title: 'Satisfaction survey', priority: 'low' },
          ],
        },
      ],
    }),
  },
  {
    id: '4',
    tenantId: null,
    title: 'Product Redesign',
    sector: TEMPLATE_SECTOR.Technology,
    description: 'Research, design, build and roll out a product redesign.',
    isSystem: true,
    createdById: 'system',
    createdAt: '2026-01-05T09:00:00Z',
    updatedAt: '2026-01-05T09:00:00Z',
    customFields: [
      {
        name: 'Platform',
        type: FIELD_TYPE.Select,
        options: ['Web', 'iOS', 'Android'],
      },
      { name: 'Affected component', type: FIELD_TYPE.Text, options: null },
      { name: 'Feature flag', type: FIELD_TYPE.Text, options: null },
    ],
    tags: [
      { name: 'UI', color: 'blue-azure' },
      { name: 'UX', color: 'purple-violet' },
      { name: 'Tech debt', color: 'orange-amber' },
      { name: 'Breaking change', color: 'red-ember' },
    ],
    boards: singleBoard({
      name: 'Main board',
      statuses: [
        { name: 'Backlog', color: 'gray-slate', type: 'Todo', order: 0 },
        { name: 'In Design', color: 'purple-violet', type: 'InProgress', order: 1 },
        { name: 'In Development', color: 'blue-azure', type: 'InProgress', order: 2 },
        { name: 'Shipped', color: 'green-emerald', type: 'Done', order: 3 },
      ],
      views: withBoardView(
        { type: BOARD_VIEW_TYPE.List, name: 'List' },
        { type: BOARD_VIEW_TYPE.Timeline, name: 'Timeline' },
      ),
      groups: [
        {
          name: 'Research',
          order: 0,
          tasks: [
            { title: 'Analyze user feedback', priority: 'high' },
            { title: 'Audit current product', priority: 'medium' },
            { title: 'Competitive benchmarking', priority: 'medium' },
            { title: 'Define key problems', priority: 'high' },
          ],
        },
        {
          name: 'Design',
          order: 1,
          tasks: [
            { title: 'Create wireframes', priority: 'high' },
            { title: 'Design component system', priority: 'high' },
            { title: 'Prototype core flows', priority: 'medium' },
            { title: 'Validate with user testing', priority: 'high' },
            { title: 'Iterate on visual design', priority: 'medium' },
          ],
        },
        {
          name: 'Development',
          order: 2,
          tasks: [
            { title: 'Implement new design', priority: 'high' },
            { title: 'Migrate legacy components', priority: 'medium' },
            { title: 'Visual regression testing', priority: 'medium' },
            { title: 'Optimize performance', priority: 'medium' },
          ],
        },
        {
          name: 'Launch',
          order: 3,
          tasks: [
            { title: 'Document changes', priority: 'low' },
            { title: 'Ship redesign', priority: 'high' },
          ],
        },
      ],
    }),
  },
  {
    id: '5',
    tenantId: null,
    title: 'Production Order',
    sector: TEMPLATE_SECTOR.Industrial,
    description: 'Track a manufacturing order from planning to dispatch.',
    isSystem: true,
    createdById: 'system',
    createdAt: '2026-01-05T09:00:00Z',
    updatedAt: '2026-01-05T09:00:00Z',
    customFields: [
      {
        name: 'Production line',
        type: FIELD_TYPE.Select,
        options: ['Line 1', 'Line 2', 'Line 3'],
      },
      { name: 'Quantity', type: FIELD_TYPE.Number, options: null },
      { name: 'Delivery date', type: FIELD_TYPE.Date, options: null },
    ],
    tags: [
      { name: 'Urgent', color: 'red-ember' },
      { name: 'Rework', color: 'orange-amber' },
      { name: 'Export', color: 'blue-azure' },
      { name: 'Certified quality', color: 'green-emerald' },
    ],
    boards: singleBoard({
      name: 'Main board',
      statuses: [
        { name: 'Pending', color: 'gray-slate', type: 'Todo', order: 0 },
        { name: 'In Manufacturing', color: 'orange-amber', type: 'InProgress', order: 1 },
        { name: 'Quality Control', color: 'blue-azure', type: 'InProgress', order: 2 },
        { name: 'Ready for Dispatch', color: 'green-emerald', type: 'Done', order: 3 },
      ],
      views: withBoardView({ type: BOARD_VIEW_TYPE.Timeline, name: 'Timeline' }),
      groups: [
        {
          name: 'Planning',
          order: 0,
          tasks: [
            { title: 'Review purchase order', priority: 'high' },
            { title: 'Check material availability', priority: 'high' },
            { title: 'Schedule production line', priority: 'medium' },
          ],
        },
        {
          name: 'Manufacturing',
          order: 1,
          tasks: [
            { title: 'Prepare raw materials', priority: 'medium' },
            { title: 'Run manufacturing process', priority: 'high' },
            { title: 'Assemble components', priority: 'medium' },
          ],
        },
        {
          name: 'Quality',
          order: 2,
          tasks: [
            { title: 'Quality inspection', priority: 'high' },
            { title: 'Control testing', priority: 'medium' },
          ],
        },
        {
          name: 'Delivery',
          order: 3,
          tasks: [
            { title: 'Pack product', priority: 'medium' },
            { title: 'Coordinate dispatch', priority: 'low' },
          ],
        },
      ],
    }),
  },
  {
    id: 'tenant-tpl-bug-triage',
    tenantId: 'mock-org-1',
    title: 'Weekly Bug Triage',
    sector: TEMPLATE_SECTOR.Technology,
    description: 'Internal template for our weekly bug triage project.',
    isSystem: false,
    createdById: 'mock-user-1',
    createdAt: '2026-06-01T09:00:00Z',
    updatedAt: '2026-06-10T09:00:00Z',
    customFields: [],
    tags: [{ name: 'Triaged', color: 'green-emerald' }],
    boards: singleBoard({
      name: 'Main board',
      statuses: [
        { name: 'Backlog', color: 'gray-slate', type: 'Todo', order: 0 },
        { name: 'In Progress', color: 'blue-azure', type: 'InProgress', order: 1 },
        { name: 'Done', color: 'green-emerald', type: 'Done', order: 2 },
      ],
      views: withBoardView({ type: BOARD_VIEW_TYPE.List, name: 'List' }),
      groups: [
        {
          name: 'Backlog',
          order: 0,
          tasks: [
            { title: 'Triage new reports', priority: 'high' },
            { title: 'Reproduce top issues', priority: 'medium' },
            { title: 'Assign owners', priority: 'medium' },
          ],
        },
      ],
    }),
  },
];

export function toProjectTemplateSummary(template: ProjectTemplate): ProjectTemplateSummary {
  return {
    id: template.id,
    tenantId: template.tenantId,
    title: template.title,
    sector: template.sector,
    isSystem: template.isSystem,
    updatedAt: template.updatedAt,
  };
}
